import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardActions, CardContent, Stack, CircularProgress } from '@mui/material';
import { Form } from 'components/_forms';
import { AppContext } from 'views/App/context';
import { translate as t } from 'hooks/useTranslations';
import { useQueryClient } from 'react-query';
import { enqueueSnackbar } from 'notistack';
import DTBPackageFormFields from '../_components/DTBPackageFormFields';
import useManageDTBPackages from '../_data_source/useManageDTBPackage';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';

const DTBPackageCreate: React.FC = () => {
  const navigate = useNavigate();
  const { currentOrganizationId: orgId, currentProfile } = useContext(AppContext);

  const [packageName, setPackageName] = useState('');
  const [jurisdictionId, setJurisdictionId] = useState('');
  const [manualId, setManualId] = useState('');
  const [verticalId, setVerticalId] = useState('');
  const [level, setLevel] = useState<'AGENCY' | 'JURISDICTION' | 'GLOBAL'>('GLOBAL');
  const [isCreating, setIsCreating] = useState(false);

  const queryClient = useQueryClient();
  const { create } = useManageDTBPackages();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const userId = currentProfile?.user?.id || '';

    const payload = {
      userId,
      orgId,
      name: packageName,
      verticalId: '',
      jurisdictionId: level === 'JURISDICTION' ? jurisdictionId : '',
      manualId,
      level,
    };

    setIsCreating(true);
    try {
      await create(
        orgId,
        payload,
        (response) => {
          queryClient.invalidateQueries(['dtbPackages']);
          enqueueSnackbar(t('dtbs.packages.create.success', 'DTB Package successfully created'), {
            variant: 'success',
          });
          navigate(`../${response.id}`);
        },
        (err: unknown) => console.error('Error creating DTB Package:', err)
      );
    } catch (error) {
      console.error('Error creating DTB Package:', error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <TakeoverLayout
      pageTitle={t('dtbs.packages.create.title')}
      backUrl='..'
    >
      <Card
        sx={{ maxWidth: 800, mx: 'auto', width: '100%' }}
        className='lex-form'
      >
        <Form onSubmit={handleSubmit}>
          <CardContent>
            <DTBPackageFormFields
              orgId={orgId || ''}
              packageName={packageName}
              jurisdictionId={jurisdictionId}
              manualId={manualId}
              verticalId={verticalId}
              level={level}
              isNew={true}
              onPackageNameChange={setPackageName}
              onJurisdictionChange={setJurisdictionId}
              onManualChange={setManualId}
              onVerticalChange={setVerticalId}
              onLevelChange={setLevel}
            />
          </CardContent>
          <CardActions>
            <Box />
            <Stack
              direction='row'
              spacing={2}
            >
              <Button
                color='neutral'
                href='..'
              >
                {t('common.cancel')}
              </Button>
              <Button
                disabled={isCreating || !packageName || (level === 'JURISDICTION' && !jurisdictionId)}
                component='button'
                type='submit'
                variant='contained'
                endIcon={isCreating ? <CircularProgress size={16} /> : undefined}
              >
                {t('common.save')}
              </Button>
            </Stack>
          </CardActions>
        </Form>
      </Card>
    </TakeoverLayout>
  );
};

export default DTBPackageCreate;
